import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Input,
  Button,
  Select,
  Modal,
  Form,
  Tag,
  Space,
  Row,
  Col,
  Typography,
  Tooltip,
  message,
  Empty,
  Descriptions,
  Divider,
  Alert,
  Avatar,
  Statistic,
  Pagination,
  Tabs,
  Switch
} from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  CalendarOutlined,
  TeamOutlined,
  AuditOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ExperimentOutlined,
  MonitorOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  FileTextOutlined,
  CloudServerOutlined,
  HddOutlined,
  InfoCircleOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import API from '../services/api';
import './IPPermissionManagement.css';

const { Search } = Input;
const { Option } = Select;
const { Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const IPPermissionManagement = () => {
  // 标签页状态
  const [activeTab, setActiveTab] = useState('requests');

  // 原有的数据状态
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const limit = 10;

  // 自动审批配置相关状态
  const [autoConfigs, setAutoConfigs] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configCurrentPage, setConfigCurrentPage] = useState(1);
  const [totalConfigs, setTotalConfigs] = useState(0);
  const [configSearchTerm, setConfigSearchTerm] = useState('');

  // 托管IP监控服务相关状态
  const [monitorStatus, setMonitorStatus] = useState(null);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [monitorStatistics, setMonitorStatistics] = useState(null);
  const [recentColocationRequests, setRecentColocationRequests] = useState([]);
  const [colocationLoading, setColocationLoading] = useState(false);

  // VPS和物理机IP监控服务相关状态
  const [vpsPhysicalMonitorStatus, setVpsPhysicalMonitorStatus] = useState(null);
  const [vpsPhysicalMonitorLoading, setVpsPhysicalMonitorLoading] = useState(false);
  const [vpsPhysicalStatistics, setVpsPhysicalStatistics] = useState(null);
  const [vpsPhysicalRecentRequests, setVpsPhysicalRecentRequests] = useState([]);
  const [vpsPhysicalCheckReport, setVpsPhysicalCheckReport] = useState(null);
  const [vpsPhysicalDataLoading, setVpsPhysicalDataLoading] = useState(false);

  // 筛选和搜索状态
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // 模态框状态
  const [detailVisible, setDetailVisible] = useState(false);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [batchVisible, setBatchVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [batchDeleteVisible, setBatchDeleteVisible] = useState(false);
  
  // 自动审批配置模态框状态
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configDeleteVisible, setConfigDeleteVisible] = useState(false);
  const [batchConfigDeleteVisible, setBatchConfigDeleteVisible] = useState(false);
  const [testIPVisible, setTestIPVisible] = useState(false);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  
  // 当前操作的申请
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // 当前操作的配置
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configSubmitLoading, setConfigSubmitLoading] = useState(false);
  const [configDeleteLoading, setConfigDeleteLoading] = useState(false);
  const [batchConfigDeleteLoading, setBatchConfigDeleteLoading] = useState(false);

  // IP详情分页状态
  const [ipDetailPage, setIpDetailPage] = useState(1);
  const ipDetailPageSize = 20;

  // 批量操作状态
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // 表单实例
  const [reviewForm] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [testIPForm] = Form.useForm();

  // 获取IP权限申请列表
  const fetchRequests = useCallback(async (page = 1, status = '', search = '') => {
    setLoading(true);
    try {
      const response = await API.get('/api/admin/ip-permission-requests', {
        params: {
          page,
          limit,
          status,
          search
        }
      });
      
      setRequests(response.data.requests);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalRequests(response.data.totalRequests);
    } catch (error) {
      console.error('获取IP权限申请列表失败:', error);
      message.error('获取申请列表失败');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // 获取自动审批配置列表
  const fetchAutoConfigs = useCallback(async (page = 1, search = '') => {
    setConfigLoading(true);
    try {
      const response = await API.get('/api/admin/auto-approval-configs', {
        params: {
          page,
          limit,
          search
        }
      });
      
      setAutoConfigs(response.data.configs);
      setConfigCurrentPage(response.data.currentPage);
      setTotalConfigs(response.data.totalConfigs);
    } catch (error) {
      console.error('获取自动审批配置列表失败:', error);
      message.error('获取配置列表失败');
    } finally {
      setConfigLoading(false);
    }
  }, [limit]);

  // 获取托管IP监控服务状态
  const fetchMonitorStatus = async () => {
    try {
      const response = await API.get('/api/admin/colocation-ip-monitor/status');
      setMonitorStatus(response.data.status);
    } catch (error) {
      console.error('获取托管IP监控服务状态失败:', error);
      message.error('获取监控服务状态失败');
    }
  };

  // 获取托管IP权限申请统计
  const fetchMonitorStatistics = async () => {
    setMonitorLoading(true);
    try {
      const response = await API.get('/api/admin/colocation-ip-monitor/statistics');
      setMonitorStatistics(response.data.statistics);
    } catch (error) {
      console.error('获取托管IP权限申请统计失败:', error);
      message.error('获取统计数据失败');
    } finally {
      setMonitorLoading(false);
    }
  };

  // 获取最近的托管IP权限申请
  const fetchRecentColocationRequests = async () => {
    setColocationLoading(true);
    try {
      const response = await API.get('/api/admin/colocation-ip-monitor/recent-requests', {
        params: { limit: 10 }
      });
      setRecentColocationRequests(response.data.requests);
    } catch (error) {
      console.error('获取最近托管IP权限申请失败:', error);
      message.error('获取申请列表失败');
    } finally {
      setColocationLoading(false);
    }
  };

  // 启动托管IP监控服务
  const handleStartMonitor = async () => {
    setMonitorLoading(true);
    try {
      const response = await API.post('/api/admin/colocation-ip-monitor/start');
      message.success(response.data.message);
      setMonitorStatus(response.data.status);
    } catch (error) {
      message.error(error.response?.data?.message || '启动监控服务失败');
    } finally {
      setMonitorLoading(false);
    }
  };

  // 停止托管IP监控服务
  const handleStopMonitor = async () => {
    setMonitorLoading(true);
    try {
      const response = await API.post('/api/admin/colocation-ip-monitor/stop');
      message.success(response.data.message);
      setMonitorStatus(response.data.status);
    } catch (error) {
      message.error(error.response?.data?.message || '停止监控服务失败');
    } finally {
      setMonitorLoading(false);
    }
  };

  // 手动触发托管IP权限检查
  const handleManualCheck = async () => {
    try {
      const response = await API.post('/api/admin/colocation-ip-monitor/manual-check');
      message.success(response.data.message);
    } catch (error) {
      message.error(error.response?.data?.message || '手动检查失败');
    }
  };

  // 获取VPS和物理机IP监控服务状态
  const fetchVpsPhysicalMonitorStatus = async () => {
    try {
      const response = await API.get('/api/admin/vps-physical-ip-monitor/status');
      setVpsPhysicalMonitorStatus(response.data.status);
    } catch (error) {
      console.error('获取VPS和物理机IP监控服务状态失败:', error);
      message.error('获取VPS和物理机监控服务状态失败');
    }
  };

  // 启动VPS和物理机IP监控服务
  const handleStartVpsPhysicalMonitor = async () => {
    setVpsPhysicalMonitorLoading(true);
    try {
      const response = await API.post('/api/admin/vps-physical-ip-monitor/start');
      message.success(response.data.message);
      setVpsPhysicalMonitorStatus(response.data.status);
    } catch (error) {
      message.error(error.response?.data?.message || '启动VPS和物理机监控服务失败');
    } finally {
      setVpsPhysicalMonitorLoading(false);
    }
  };

  // 停止VPS和物理机IP监控服务
  const handleStopVpsPhysicalMonitor = async () => {
    setVpsPhysicalMonitorLoading(true);
    try {
      const response = await API.post('/api/admin/vps-physical-ip-monitor/stop');
      message.success(response.data.message);
      setVpsPhysicalMonitorStatus(response.data.status);
    } catch (error) {
      message.error(error.response?.data?.message || '停止VPS和物理机监控服务失败');
    } finally {
      setVpsPhysicalMonitorLoading(false);
    }
  };

  // 手动触发VPS和物理机IP检查
  const handleVpsPhysicalManualCheck = async () => {
    try {
      const response = await API.post('/api/admin/vps-physical-ip-monitor/manual-check');
      message.success(response.data.message);
    } catch (error) {
      message.error(error.response?.data?.message || 'VPS和物理机手动检查失败');
    }
  };

  // 获取VPS和物理机监控统计数据
  const fetchVpsPhysicalStatistics = async () => {
    try {
      const response = await API.get('/api/admin/vps-physical-ip-monitor/statistics');
      setVpsPhysicalStatistics(response.data.statistics);
    } catch (error) {
      console.error('获取VPS和物理机监控统计数据失败:', error);
      message.error('获取统计数据失败');
    }
  };

  // 获取VPS和物理机最近申请列表
  const fetchVpsPhysicalRecentRequests = async () => {
    setVpsPhysicalDataLoading(true);
    try {
      const response = await API.get('/api/admin/vps-physical-ip-monitor/recent-requests?limit=10');
      setVpsPhysicalRecentRequests(response.data.requests);
    } catch (error) {
      console.error('获取VPS和物理机最近申请失败:', error);
      message.error('获取最近申请失败');
    } finally {
      setVpsPhysicalDataLoading(false);
    }
  };

  // 获取VPS和物理机监控详细检查报告
  const fetchVpsPhysicalCheckReport = async () => {
    try {
      const response = await API.get('/api/admin/vps-physical-ip-monitor/check-report');
      setVpsPhysicalCheckReport(response.data.report);
    } catch (error) {
      console.error('获取VPS和物理机检查报告失败:', error);
      message.error('获取检查报告失败');
    }
  };

  // 获取申请详情
  const fetchRequestDetail = async (requestId) => {
    try {
      const response = await API.get(`/api/admin/ip-permission-requests/${requestId}`);
      setSelectedRequest(response.data);
      setIpDetailPage(1); // 重置IP详情分页
      setDetailVisible(true);
    } catch (error) {
      console.error('获取申请详情失败:', error);
      message.error('获取申请详情失败');
    }
  };

  // 创建或更新自动审批配置
  const handleConfigSubmit = async (values) => {
    setConfigSubmitLoading(true);
    try {
      let response;
      if (isEditingConfig && selectedConfig) {
        // 更新配置
        response = await API.put(`/api/admin/auto-approval-configs/${selectedConfig.id}`, values);
      } else {
        // 创建配置
        response = await API.post('/api/admin/auto-approval-configs', values);
      }

      message.success(response.data.message);
      setConfigModalVisible(false);
      configForm.resetFields();
      setSelectedConfig(null);
      setIsEditingConfig(false);
      
      // 刷新配置列表
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setConfigSubmitLoading(false);
    }
  };

  // 删除单个配置
  const handleConfigDelete = async () => {
    setConfigDeleteLoading(true);
    try {
      const response = await API.delete(`/api/admin/auto-approval-configs/${selectedConfig.id}`);
      message.success(response.data.message);
      setConfigDeleteVisible(false);
      setSelectedConfig(null);
      
      // 刷新配置列表
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    } finally {
      setConfigDeleteLoading(false);
    }
  };

  // 批量删除配置
  const handleBatchConfigDelete = async () => {
    setBatchConfigDeleteLoading(true);
    try {
      // 逐个删除选中的配置
      const promises = selectedConfigIds.map(id => 
        API.delete(`/api/admin/auto-approval-configs/${id}`)
      );
      
      await Promise.all(promises);
      message.success(`成功删除 ${selectedConfigIds.length} 个配置`);
      setBatchConfigDeleteVisible(false);
      setSelectedConfigIds([]);
      
      // 刷新配置列表
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } catch (error) {
      message.error('批量删除失败');
    } finally {
      setBatchConfigDeleteLoading(false);
    }
  };

  // 批量启用/禁用配置
  const handleBatchToggleConfig = async (isEnabled) => {
    try {
      const response = await API.patch('/api/admin/auto-approval-configs/batch-toggle', {
        configIds: selectedConfigIds,
        isEnabled
      });
      
      message.success(response.data.message);
      setSelectedConfigIds([]);
      
      // 刷新配置列表
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '批量操作失败');
    }
  };

  // 切换单个配置的启用状态
  const handleToggleConfig = async (config, isEnabled) => {
    try {
      const response = await API.put(`/api/admin/auto-approval-configs/${config.id}`, {
        isEnabled
      });
      
      message.success(response.data.message);
      
      // 刷新配置列表
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 测试IP
  const handleTestIP = async (values) => {
    try {
      const response = await API.post('/api/admin/auto-approval-configs/test-ip', {
        ip: values.ip
      });
      
      const { inAutoApprovalRange, matchedRange, description } = response.data;
      
      if (inAutoApprovalRange) {
        Modal.success({
          title: 'IP测试结果',
          content: (
            <div>
              <p><strong>IP地址：</strong>{values.ip}</p>
              <p><strong>匹配结果：</strong><Tag color="green">在自动审批范围内</Tag></p>
              <p><strong>匹配范围：</strong>{matchedRange}</p>
              <p><strong>描述：</strong>{description}</p>
            </div>
          ),
        });
      } else {
        Modal.info({
          title: 'IP测试结果',
          content: (
            <div>
              <p><strong>IP地址：</strong>{values.ip}</p>
              <p><strong>匹配结果：</strong><Tag color="orange">不在自动审批范围内</Tag></p>
              <p>该IP地址需要手动审核。</p>
            </div>
          ),
        });
      }
      
      setTestIPVisible(false);
      testIPForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || '测试失败');
    }
  };

  // 单个审核申请
  const handleReview = async (values) => {
    setReviewLoading(true);
    try {
      const endpoint = reviewAction === 'approve' ? 'approve' : 'reject';
      const response = await API.post(`/api/admin/ip-permission-requests/${selectedRequest.id}/${endpoint}`, {
        reviewComment: values.reviewComment?.trim() || ''
      });

      message.success(response.data.message);
      setReviewVisible(false);
      reviewForm.resetFields();
      setSelectedRequest(null);
      setReviewAction('');
      
      // 刷新列表
      fetchRequests(currentPage, statusFilter, searchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '审核操作失败');
    } finally {
      setReviewLoading(false);
    }
  };

  // 批量审核申请
  const handleBatchReview = async (values) => {
    setBatchLoading(true);
    try {
      const analysis = getSelectedRecordsAnalysis();
      // 只处理待审核的记录
      const response = await API.post('/api/admin/ip-permission-requests/batch-action', {
        requestIds: analysis.pendingIds,
        action: values.action,
        reviewComment: values.reviewComment?.trim() || ''
      });

      message.success(response.data.message);
      setBatchVisible(false);
      batchForm.resetFields();
      setSelectedRequestIds([]);
      
      // 刷新列表
      fetchRequests(currentPage, statusFilter, searchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '批量操作失败');
    } finally {
      setBatchLoading(false);
    }
  };

  // 删除单个申请
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await API.delete(`/api/admin/ip-permission-requests/${selectedRequest.id}`);
      message.success(response.data.message);
      setDeleteVisible(false);
      setSelectedRequest(null);
      
      // 刷新列表
      fetchRequests(currentPage, statusFilter, searchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '删除操作失败');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 批量删除申请
  const handleBatchDelete = async () => {
    setBatchDeleteLoading(true);
    try {
      const response = await API.delete('/api/admin/ip-permission-requests/batch-delete', {
        data: { requestIds: selectedRequestIds }
      });
      message.success(response.data.message);
      setBatchDeleteVisible(false);
      setSelectedRequestIds([]);
      
      // 刷新列表
      fetchRequests(currentPage, statusFilter, searchTerm);
    } catch (error) {
      message.error(error.response?.data?.message || '批量删除操作失败');
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // 处理配置搜索
  const handleConfigSearch = (value) => {
    setConfigSearchTerm(value);
    setConfigCurrentPage(1);
  };

  // 处理状态筛选
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // 处理刷新
  const handleRefresh = () => {
    if (activeTab === 'requests') {
      fetchRequests(currentPage, statusFilter, searchTerm);
    } else if (activeTab === 'configs') {
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } else if (activeTab === 'monitor') {
      fetchMonitorStatus();
      fetchMonitorStatistics();
      fetchRecentColocationRequests();
    } else if (activeTab === 'vps-physical-monitor') {
      fetchVpsPhysicalMonitorStatus();
      fetchVpsPhysicalStatistics();
      fetchVpsPhysicalRecentRequests();
      fetchVpsPhysicalCheckReport();
    }
  };

  // 获取状态颜色和文本
  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { color: 'orange', text: '待审核', icon: <ClockCircleOutlined /> };
      case 'approved':
        return { color: 'green', text: '已通过', icon: <CheckCircleOutlined /> };
      case 'rejected':
        return { color: 'red', text: '已拒绝', icon: <CloseCircleOutlined /> };
      default:
        return { color: 'default', text: '未知', icon: <ExclamationCircleOutlined /> };
    }
  };

  // 申请列表表格列定义
  const requestColumns = [
    {
      title: '申请人',
      dataIndex: 'applicant',
      key: 'applicant',
      width: 150,
      render: (applicant) => (
        <div className="applicant-info">
          <div className="applicant-main">
            <Avatar size="small" icon={<UserOutlined />} className="me-2" />
            <div>
              <div className="applicant-name">{applicant.username}</div>
              <div className="applicant-email">{applicant.email}</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '申请IP',
      dataIndex: 'ipAddresses',
      key: 'ipAddresses',
      width: 120,
      render: (ipAddresses, record) => (
        <Tag color="blue" icon={<GlobalOutlined />} className="ip-count-tag">
          {record.ipCount} 个IP
        </Tag>
      ),
    },
    {
      title: '云平台ID',
      dataIndex: 'cloudPlatformUID',
      key: 'cloudPlatformUID',
      width: 140,
      render: (text) => (
        <Tag icon={<TeamOutlined />} color="purple">
          {text}
        </Tag>
      ),
    },
    {
      title: 'IP类型',
      dataIndex: 'ipType',
      key: 'ipType',
      width: 100,
      render: (ipType) => {
        const typeMap = {
          'vps': { text: 'VPS', color: 'blue' },
          'physical': { text: '物理机', color: 'green' },
          'colocation': { text: '托管', color: 'orange' }
        };
        const config = typeMap[ipType] || { text: ipType || 'VPS', color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '申请理由',
      dataIndex: 'applicationReason',
      key: 'applicationReason',
      width: 200,
      render: (text) => (
        <div className="reason-text-table">
          {text}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <div className="date-display">
          <CalendarOutlined className="date-icon" />
          <div>
            <div>{new Date(date).toLocaleDateString()}</div>
            <div className="time-detail">{new Date(date).toLocaleTimeString()}</div>
          </div>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => fetchRequestDetail(record.id)}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="通过">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    setSelectedRequest(record);
                    setReviewAction('approve');
                    setReviewVisible(true);
                  }}
                  className="approve-btn"
                />
              </Tooltip>
              <Tooltip title="驳回">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setSelectedRequest(record);
                    setReviewAction('reject');
                    setReviewVisible(true);
                  }}
                  className="reject-btn"
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="删除">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setSelectedRequest(record);
                setDeleteVisible(true);
              }}
              className="delete-btn"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 自动审批配置表格列定义
  const configColumns = [
    {
      title: 'IP范围',
      dataIndex: 'ipRange',
      key: 'ipRange',
      width: 200,
      render: (text) => (
        <code className="ip-range-code">{text}</code>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (text) => (
        <div className="config-description">
          {text}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (isEnabled, record) => (
        <Switch
          checked={isEnabled}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => handleToggleConfig(record, checked)}
        />
      ),
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (createdBy) => (
        <div>
          <div>{createdBy.username}</div>
          <div className="text-secondary small">{createdBy.email}</div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <div className="date-display">
          <CalendarOutlined className="date-icon" />
          <div>
            <div>{new Date(date).toLocaleDateString()}</div>
            <div className="time-detail">{new Date(date).toLocaleTimeString()}</div>
          </div>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedConfig(record);
                setIsEditingConfig(true);
                configForm.setFieldsValue({
                  ipRange: record.ipRange,
                  description: record.description,
                  isEnabled: record.isEnabled
                });
                setConfigModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setSelectedConfig(record);
                setConfigDeleteVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 申请列表行选择配置
  const requestRowSelection = {
    selectedRowKeys: selectedRequestIds,
    onChange: (selectedRowKeys) => {
      setSelectedRequestIds(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: false, // 允许选择所有记录进行删除
    }),
  };

  // 配置列表行选择配置
  const configRowSelection = {
    selectedRowKeys: selectedConfigIds,
    onChange: (selectedRowKeys) => {
      setSelectedConfigIds(selectedRowKeys);
    },
  };

  // 统计数据
  const getStatistics = () => {
    const pending = requests.filter(r => r.status === 'pending').length;
    const approved = requests.filter(r => r.status === 'approved').length;
    const rejected = requests.filter(r => r.status === 'rejected').length;
    
    return { pending, approved, rejected };
  };

  // 获取配置统计数据
  const getConfigStatistics = () => {
    const enabled = autoConfigs.filter(c => c.isEnabled).length;
    const disabled = autoConfigs.filter(c => !c.isEnabled).length;
    
    return { enabled, disabled, total: autoConfigs.length };
  };

  // 获取选中记录的状态分析
  const getSelectedRecordsAnalysis = () => {
    const selectedRecords = requests.filter(request => selectedRequestIds.includes(request.id));
    const pendingRecords = selectedRecords.filter(r => r.status === 'pending');
    const reviewedRecords = selectedRecords.filter(r => r.status !== 'pending');
    
    return {
      total: selectedRecords.length,
      pending: pendingRecords.length,
      reviewed: reviewedRecords.length,
      pendingIds: pendingRecords.map(r => r.id),
      reviewedIds: reviewedRecords.map(r => r.id),
      hasPending: pendingRecords.length > 0,
      hasReviewed: reviewedRecords.length > 0
    };
  };

  // 获取选中配置的状态分析
  const getSelectedConfigAnalysis = () => {
    const selectedConfigs = autoConfigs.filter(config => selectedConfigIds.includes(config.id));
    const enabledConfigs = selectedConfigs.filter(c => c.isEnabled);
    const disabledConfigs = selectedConfigs.filter(c => !c.isEnabled);
    
    return {
      total: selectedConfigs.length,
      enabled: enabledConfigs.length,
      disabled: disabledConfigs.length,
      hasEnabled: enabledConfigs.length > 0,
      hasDisabled: disabledConfigs.length > 0
    };
  };

  // 初始加载数据
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests(currentPage, statusFilter, searchTerm);
    } else if (activeTab === 'configs') {
      fetchAutoConfigs(configCurrentPage, configSearchTerm);
    } else if (activeTab === 'monitor') {
      fetchMonitorStatus();
      fetchMonitorStatistics();
      fetchRecentColocationRequests();
    } else if (activeTab === 'vps-physical-monitor') {
      fetchVpsPhysicalMonitorStatus();
      fetchVpsPhysicalStatistics();
      fetchVpsPhysicalRecentRequests();
      fetchVpsPhysicalCheckReport();
    }
  }, [activeTab, currentPage, statusFilter, searchTerm, configCurrentPage, configSearchTerm, fetchRequests, fetchAutoConfigs]);

  const statistics = getStatistics();
  const configStatistics = getConfigStatistics();

  return (
    <div className="ip-permission-management">
      {/* 页面头部 */}
      <Card className="page-header-card">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">IP权限管理</h1>
            <div className="page-subtitle">
              {activeTab === 'requests' ? 
                `共 ${totalRequests} 个申请记录` : 
                activeTab === 'configs' ?
                `共 ${totalConfigs} 个自动审批配置` :
                activeTab === 'monitor' ?
                '托管IP权限监控服务管理' :
                activeTab === 'vps-physical-monitor' ?
                'VPS和物理机IP监控服务管理' :
                '监控服务管理'
              }
            </div>
          </div>
          <div className="page-header-extra">
            {activeTab === 'requests' && selectedRequestIds.length > 0 && (() => {
              const analysis = getSelectedRecordsAnalysis();
              return (
                <Space>
                  {analysis.hasPending && (
                    <Button
                      type="primary"
                      icon={<AuditOutlined />}
                      onClick={() => setBatchVisible(true)}
                    >
                      批量处理 ({analysis.pending})
                    </Button>
                  )}
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setBatchDeleteVisible(true)}
                  >
                    批量删除 ({analysis.total})
                  </Button>
                </Space>
              );
            })()}
            
            {activeTab === 'configs' && (
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedConfig(null);
                    setIsEditingConfig(false);
                    configForm.resetFields();
                    setConfigModalVisible(true);
                  }}
                >
                  新增配置
                </Button>
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={() => {
                    testIPForm.resetFields();
                    setTestIPVisible(true);
                  }}
                >
                  测试IP
                </Button>
                                 {selectedConfigIds.length > 0 && (() => {
                   const analysis = getSelectedConfigAnalysis();
                   return (
                     <div className="batch-actions">
                       {analysis.hasDisabled && (
                         <Button
                           icon={<PlayCircleOutlined />}
                           onClick={() => handleBatchToggleConfig(true)}
                         >
                           批量启用 ({analysis.disabled})
                         </Button>
                       )}
                       {analysis.hasEnabled && (
                         <Button
                           icon={<PauseCircleOutlined />}
                           onClick={() => handleBatchToggleConfig(false)}
                         >
                           批量禁用 ({analysis.enabled})
                         </Button>
                       )}
                       <Button
                         danger
                         icon={<DeleteOutlined />}
                         onClick={() => setBatchConfigDeleteVisible(true)}
                       >
                         批量删除 ({analysis.total})
                       </Button>
                     </div>
                   );
                 })()}
              </Space>
            )}
          </div>
        </div>
      </Card>

      {/* 标签页 */}
      <Card className="tabs-card">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
        >
          <TabPane
            tab={<span>申请管理</span>}
            key="requests"
          />
          <TabPane
            tab={
              <span>
                <SettingOutlined />
                自动审批配置
              </span>
            }
            key="configs"
          />
          <TabPane
            tab={
              <span>
                <MonitorOutlined />
                托管IP监控
              </span>
            }
            key="monitor"
          />
          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                VPS/物理机IP监控
              </span>
            }
            key="vps-physical-monitor"
          />
        </Tabs>
      </Card>

      {/* 申请管理标签页内容 */}
      {activeTab === 'requests' && (
        <>
          {/* 统计卡片 */}
          <Row gutter={16} className="statistics-row">
            <Col span={8}>
              <Card>
                <Statistic
                  title="待审核"
                  value={statistics.pending}
                  valueStyle={{ color: '#fa8c16' }}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="已通过"
                  value={statistics.approved}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="已拒绝"
                  value={statistics.rejected}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* 搜索和筛选区域 */}
          <Card className="filter-card">
            <Row gutter={16} align="middle">
              <Col span={12}>
                <Search
                  placeholder="搜索IP地址、用户名、邮箱、云平台ID..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                  enterButton={<SearchOutlined />}
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder="状态筛选"
                  allowClear
                  style={{ width: '100%' }}
                  value={statusFilter || undefined}
                  onChange={handleStatusFilter}
                  suffixIcon={<FilterOutlined />}
                >
                  <Option value="pending">
                    <Tag color="orange" icon={<ClockCircleOutlined />}>待审核</Tag>
                  </Option>
                  <Option value="approved">
                    <Tag color="green" icon={<CheckCircleOutlined />}>已通过</Tag>
                  </Option>
                  <Option value="rejected">
                    <Tag color="red" icon={<CloseCircleOutlined />}>已拒绝</Tag>
                  </Option>
                </Select>
              </Col>
              <Col span={6} style={{ textAlign: 'right' }}>
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                >
                  刷新
                </Button>
              </Col>
            </Row>
          </Card>

          {/* 申请列表 */}
          <Card className="table-card">
            <Table
              rowSelection={requestRowSelection}
              columns={requestColumns}
              dataSource={requests}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                total: totalRequests,
                pageSize: limit,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                onChange: (page) => setCurrentPage(page),
              }}
              scroll={{ x: 1200 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无申请记录"
                  />
                ),
              }}
            />
          </Card>
        </>
      )}

      {/* 自动审批配置标签页内容 */}
      {activeTab === 'configs' && (
        <>
          {/* 配置统计卡片 */}
          <Row gutter={16} className="statistics-row">
            <Col span={8}>
              <Card>
                <Statistic
                  title="已启用"
                  value={configStatistics.enabled}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="已禁用"
                  value={configStatistics.disabled}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="总配置"
                  value={configStatistics.total}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<SettingOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* 配置搜索区域 */}
          <Card className="filter-card">
            <Row gutter={16} align="middle">
              <Col span={18}>
                <Search
                  placeholder="搜索IP范围、描述..."
                  allowClear
                  onSearch={handleConfigSearch}
                  style={{ width: '100%' }}
                  enterButton={<SearchOutlined />}
                />
              </Col>
              <Col span={6} style={{ textAlign: 'right' }}>
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={configLoading}
                >
                  刷新
                </Button>
              </Col>
            </Row>
          </Card>

          {/* 配置列表 */}
          <Card className="table-card">
            <Table
              rowSelection={configRowSelection}
              columns={configColumns}
              dataSource={autoConfigs}
              rowKey="id"
              loading={configLoading}
              pagination={{
                current: configCurrentPage,
                total: totalConfigs,
                pageSize: limit,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                onChange: (page) => setConfigCurrentPage(page),
              }}
              scroll={{ x: 1200 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无配置记录"
                  />
                ),
              }}
            />
          </Card>
        </>
      )}

      {/* 托管IP监控标签页内容 */}
      {activeTab === 'monitor' && (
        <>
          {/* 监控服务状态卡片 */}
          <Card className="monitor-status-card" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col span={6}>
                                 <div style={{ textAlign: 'center' }}>
                   <div style={{ 
                     padding: '16px',
                     backgroundColor: monitorStatus?.isRunning ? '#f6ffed' : '#f5f5f5',
                     borderRadius: '8px',
                     border: `2px solid ${monitorStatus?.isRunning ? '#b7eb8f' : '#d9d9d9'}`,
                     marginBottom: 8
                   }}>
                     <div style={{ fontSize: '32px', marginBottom: 8 }}>
                       {monitorStatus?.isRunning ? (
                         <SafetyOutlined style={{ color: '#52c41a' }} />
                       ) : (
                         <PauseCircleOutlined style={{ color: '#999' }} />
                       )}
                     </div>
                     <div style={{ 
                       color: monitorStatus?.isRunning ? '#52c41a' : '#999',
                       fontSize: '18px',
                       fontWeight: 'bold',
                       marginBottom: 4
                     }}>
                       {monitorStatus?.isRunning ? '运行中' : '已停止'}
                     </div>
                     <div style={{ color: '#666', fontSize: '12px' }}>
                       托管IP监控服务
                     </div>
                   </div>
                 </div>
              </Col>
              <Col span={12}>
                                 {monitorStatus && (
                   <div>
                     <Descriptions size="small" column={1}>
                       <Descriptions.Item label="上次检查时间">
                         {monitorStatus.lastCheckTime ? 
                           new Date(monitorStatus.lastCheckTime).toLocaleString('zh-CN') : 
                           '从未检查'
                         }
                       </Descriptions.Item>
                                            <Descriptions.Item label="下次预定检查">
                       {(() => {
                         if (!monitorStatus.isRunning) return '服务未运行';
                         
                                                   // 根据上次检查时间计算下次检查时间（每天0点和12点）
                          if (monitorStatus.lastCheckTime) {
                            const now = new Date();
                           
                           // 获取今天的0点和12点
                           const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                           const today12 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
                           const tomorrow0 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
                           
                           let nextCheck;
                           
                           // 判断下次检查时间
                           if (now < today0) {
                             nextCheck = today0;
                           } else if (now < today12) {
                             nextCheck = today12;
                           } else {
                             nextCheck = tomorrow0;
                           }
                           
                           return nextCheck.toLocaleString('zh-CN');
                         }
                         
                         // 如果没有上次检查时间，根据当前时间判断下次检查时间
                         const now = new Date();
                         const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                         const today12 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
                         const tomorrow0 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
                         
                         let nextCheck;
                         if (now < today0) {
                           nextCheck = today0;
                         } else if (now < today12) {
                           nextCheck = today12;
                         } else {
                           nextCheck = tomorrow0;
                         }
                         
                         return nextCheck.toLocaleString('zh-CN');
                       })()}
                     </Descriptions.Item>
                                            <Descriptions.Item label="检查间隔">
                       {(() => {
                         if (!monitorStatus.checkInterval) return '未设置';
                         // 解析 cron 表达式 "0 */12 * * *" 为用户友好的文本
                         if (monitorStatus.checkInterval === '0 */12 * * *') {
                           return '每天0点和12点执行';
                         }
                         return monitorStatus.checkInterval;
                       })()}
                     </Descriptions.Item>
                     </Descriptions>
                     {monitorStatus.description && (
                       <div style={{ 
                         marginTop: 8, 
                         padding: '8px 12px', 
                         backgroundColor: '#f6f8fa', 
                         borderRadius: '4px',
                         fontSize: '12px',
                         color: '#666'
                       }}>
                         {monitorStatus.description}
                       </div>
                     )}
                   </div>
                 )}
              </Col>
              <Col span={6} style={{ textAlign: 'right' }}>
                <Space orientation="vertical">
                  {monitorStatus?.isRunning ? (
                    <Button
                      danger
                      icon={<PauseCircleOutlined />}
                      onClick={handleStopMonitor}
                      loading={monitorLoading}
                    >
                      停止监控
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleStartMonitor}
                      loading={monitorLoading}
                    >
                      启动监控
                    </Button>
                  )}
                  <Button
                    icon={<ThunderboltOutlined />}
                    onClick={handleManualCheck}
                  >
                    手动检查
                  </Button>
                </Space>
              </Col>
                        </Row>
          </Card>



          {/* 检查统计数据 */}
           {monitorStatus?.checkStats ? (
             <Card title={<><BarChartOutlined /> 检查统计信息</>} style={{ marginBottom: 16 }}>
               <Row gutter={16}>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="已检查IP总数"
                       value={monitorStatus.checkStats.totalChecked}
                       valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                       prefix={<DatabaseOutlined />}
                     />
                   </Card>
                 </Col>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="有效IP"
                       value={monitorStatus.checkStats.validIPs}
                       valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                       prefix={<CheckCircleOutlined />}
                     />
                   </Card>
                 </Col>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="无效IP"
                       value={monitorStatus.checkStats.invalidIPs}
                       valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
                       prefix={<CloseCircleOutlined />}
                     />
                   </Card>
                 </Col>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="清理成功"
                       value={monitorStatus.checkStats.cleanupSuccesses}
                       valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                       prefix={<SafetyOutlined />}
                     />
                   </Card>
                 </Col>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="清理失败"
                       value={monitorStatus.checkStats.cleanupFailures}
                       valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                       prefix={<ExclamationCircleOutlined />}
                     />
                   </Card>
                 </Col>
                 <Col span={4}>
                   <Card size="small" style={{ textAlign: 'center' }}>
                     <Statistic
                       title="检查错误"
                       value={monitorStatus.checkStats.errors}
                       valueStyle={{ color: '#f5222d', fontSize: '18px' }}
                       prefix={<CloseCircleOutlined />}
                     />
                   </Card>
                 </Col>
               </Row>
               
               {/* 检查结果进度条 */}
               {monitorStatus.checkStats.totalChecked > 0 && (
                 <div style={{ marginTop: 16 }}>
                   <div style={{ marginBottom: 8, fontWeight: 'bold', color: '#333' }}>检查结果分布</div>
                   <div style={{ 
                     display: 'flex', 
                     height: '8px', 
                     backgroundColor: '#f0f0f0',
                     borderRadius: '4px',
                     overflow: 'hidden'
                   }}>
                     <div style={{
                       width: `${(monitorStatus.checkStats.validIPs / monitorStatus.checkStats.totalChecked) * 100}%`,
                       backgroundColor: '#52c41a',
                       transition: 'width 0.3s ease'
                     }} />
                     <div style={{
                       width: `${(monitorStatus.checkStats.invalidIPs / monitorStatus.checkStats.totalChecked) * 100}%`,
                       backgroundColor: '#ff4d4f',
                       transition: 'width 0.3s ease'
                     }} />
                     <div style={{
                       width: `${(monitorStatus.checkStats.errors / monitorStatus.checkStats.totalChecked) * 100}%`,
                       backgroundColor: '#f5222d',
                       transition: 'width 0.3s ease'
                     }} />
                   </div>
                   <div style={{ 
                     display: 'flex', 
                     justifyContent: 'space-between', 
                     marginTop: 4, 
                     fontSize: '12px', 
                     color: '#666' 
                   }}>
                     <span>
                       <span style={{ color: '#52c41a' }}>● 有效</span> {Math.round((monitorStatus.checkStats.validIPs / monitorStatus.checkStats.totalChecked) * 100)}%
                     </span>
                     <span>
                       <span style={{ color: '#ff4d4f' }}>● 无效</span> {Math.round((monitorStatus.checkStats.invalidIPs / monitorStatus.checkStats.totalChecked) * 100)}%
                     </span>
                     <span>
                       <span style={{ color: '#f5222d' }}>● 错误</span> {Math.round((monitorStatus.checkStats.errors / monitorStatus.checkStats.totalChecked) * 100)}%
                     </span>
                   </div>
                 </div>
                                )}
               </Card>
             ) : monitorStatus && (
               <Card title={<><BarChartOutlined /> 检查统计信息</>} style={{ marginBottom: 16 }}>
                 <Empty
                   image={Empty.PRESENTED_IMAGE_SIMPLE}
                   description="暂无检查统计数据，请先执行一次检查操作"
                   style={{ padding: '40px 0' }}
                 />
               </Card>
             )}

           {/* 申请统计数据 */}
           {monitorStatistics && (
             <Row gutter={16} className="statistics-row">
               <Col span={6}>
                 <Card>
                   <Statistic
                     title="总申请数"
                     value={monitorStatistics.totalRequests}
                     valueStyle={{ color: '#1890ff' }}
                     prefix={<TeamOutlined />}
                   />
                 </Card>
               </Col>
               <Col span={6}>
                 <Card>
                   <Statistic
                     title="已通过"
                     value={monitorStatistics.approvedRequests}
                     valueStyle={{ color: '#52c41a' }}
                     prefix={<CheckCircleOutlined />}
                   />
                 </Card>
               </Col>
               <Col span={6}>
                 <Card>
                   <Statistic
                     title="待审核"
                     value={monitorStatistics.pendingRequests}
                     valueStyle={{ color: '#fa8c16' }}
                     prefix={<ClockCircleOutlined />}
                   />
                 </Card>
               </Col>
               <Col span={6}>
                 <Card>
                   <Statistic
                     title="已拒绝"
                     value={monitorStatistics.rejectedRequests}
                     valueStyle={{ color: '#ff4d4f' }}
                     prefix={<CloseCircleOutlined />}
                   />
                 </Card>
               </Col>
             </Row>
           )}

          {/* 服务器统计 */}
          {monitorStatistics?.serverStats && monitorStatistics.serverStats.length > 0 && (
            <Card title={<><BarChartOutlined /> 服务器IP分布统计</>} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                {monitorStatistics.serverStats.slice(0, 8).map((server, index) => (
                  <Col span={6} key={server._id || index} style={{ marginBottom: 16 }}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: 4 }}>
                        {server._id || '未知服务器'}
                      </div>
                      <div style={{ color: '#666', fontSize: '12px', marginBottom: 8 }}>
                        申请: {server.requestCount}次 | IP: {server.totalIPs}个
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: '4px', 
                        backgroundColor: '#f0f0f0', 
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${Math.min(100, (server.totalIPs / Math.max(...monitorStatistics.serverStats.map(s => s.totalIPs))) * 100)}%`,
                          height: '100%',
                          backgroundColor: '#1890ff',
                          borderRadius: '2px'
                        }} />
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* 最近申请列表 */}
          <Card 
            title={<><SafetyOutlined /> 最近托管IP权限申请</>}
            extra={
              <Button
                type="default"
                icon={<SyncOutlined />}
                onClick={() => fetchRecentColocationRequests()}
                loading={colocationLoading}
                size="small"
              >
                刷新
              </Button>
            }
          >
            <Table
              columns={[
                {
                  title: '服务器ID',
                  dataIndex: 'serverId',
                  key: 'serverId',
                  width: 120,
                  render: (text) => (
                    <code style={{ 
                      background: '#f5f5f5', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace'
                    }}>
                      {text}
                    </code>
                  ),
                },
                {
                  title: '申请人',
                  dataIndex: 'applicant',
                  key: 'applicant',
                  width: 150,
                  render: (applicant) => (
                    <div>
                      <div>{applicant.username}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{applicant.email}</div>
                    </div>
                  ),
                },
                {
                  title: 'IP数量',
                  dataIndex: 'ipCount',
                  key: 'ipCount',
                  width: 80,
                  render: (count) => (
                    <Tag color="blue">{count}个</Tag>
                  ),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => {
                    const config = getStatusConfig(status);
                    return (
                      <Tag color={config.color} icon={config.icon}>
                        {config.text}
                      </Tag>
                    );
                  },
                },
                {
                  title: '申请时间',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 160,
                  render: (date) => (
                    <div style={{ fontSize: '12px' }}>
                      <div>{new Date(date).toLocaleDateString()}</div>
                      <div style={{ color: '#666' }}>{new Date(date).toLocaleTimeString()}</div>
                    </div>
                  ),
                },
                {
                  title: '审核时间',
                  dataIndex: 'reviewedAt',
                  key: 'reviewedAt',
                  width: 160,
                  render: (date) => date ? (
                    <div style={{ fontSize: '12px' }}>
                      <div>{new Date(date).toLocaleDateString()}</div>
                      <div style={{ color: '#666' }}>{new Date(date).toLocaleTimeString()}</div>
                    </div>
                  ) : '-',
                },
                {
                  title: '操作',
                  key: 'actions',
                  width: 80,
                  render: (_, record) => (
                    <Tooltip title="查看详情">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => fetchRequestDetail(record.id)}
                        size="small"
                      />
                    </Tooltip>
                  ),
                },
              ]}
              dataSource={recentColocationRequests}
              rowKey="id"
              loading={colocationLoading}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无托管IP权限申请记录"
                  />
                ),
              }}
            />
          </Card>
        </>
      )}

      {/* VPS/物理机监控标签页内容 */}
      {activeTab === 'vps-physical-monitor' && (
        <>
          {/* VPS和物理机监控服务状态卡片 */}
          <Card className="monitor-status-card" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    padding: '16px',
                    backgroundColor: vpsPhysicalMonitorStatus?.isRunning ? '#f6ffed' : '#f5f5f5',
                    borderRadius: '8px',
                    border: `2px solid ${vpsPhysicalMonitorStatus?.isRunning ? '#b7eb8f' : '#d9d9d9'}`,
                    marginBottom: 8
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: 8 }}>
                      {vpsPhysicalMonitorStatus?.isRunning ? (
                        <DatabaseOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <PauseCircleOutlined style={{ color: '#999' }} />
                      )}
                    </div>
                    <div style={{ 
                      color: vpsPhysicalMonitorStatus?.isRunning ? '#52c41a' : '#999',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      marginBottom: 4
                    }}>
                      {vpsPhysicalMonitorStatus?.isRunning ? '运行中' : '已停止'}
                    </div>
                    <div style={{ color: '#666', fontSize: '12px' }}>
                      VPS和物理机IP监控服务
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                {vpsPhysicalMonitorStatus && (
                  <div>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="上次检查时间">
                        {vpsPhysicalMonitorStatus.lastCheckTime ? 
                          new Date(vpsPhysicalMonitorStatus.lastCheckTime).toLocaleString('zh-CN') : 
                          '从未检查'
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="下次预定检查">
                        {(() => {
                          if (!vpsPhysicalMonitorStatus.isRunning) return '服务未运行';
                          
                          // VPS和物理机监控可能有不同的检查间隔
                          if (vpsPhysicalMonitorStatus.lastCheckTime) {
                            const now = new Date();
                            // 假设VPS和物理机监控也是每12小时检查一次
                            const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                            const today12 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
                            const tomorrow0 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
                            
                            let nextCheck;
                            if (now < today0) {
                              nextCheck = today0;
                            } else if (now < today12) {
                              nextCheck = today12;
                            } else {
                              nextCheck = tomorrow0;
                            }
                            
                            return nextCheck.toLocaleString('zh-CN');
                          }
                          
                          const now = new Date();
                          const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
                          const today12 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
                          const tomorrow0 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
                          
                          let nextCheck;
                          if (now < today0) {
                            nextCheck = today0;
                          } else if (now < today12) {
                            nextCheck = today12;
                          } else {
                            nextCheck = tomorrow0;
                          }
                          
                          return nextCheck.toLocaleString('zh-CN');
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="检查间隔">
                        {(() => {
                          if (!vpsPhysicalMonitorStatus.checkInterval) return '未设置';
                          if (vpsPhysicalMonitorStatus.checkInterval === '0 */12 * * *') {
                            return '每天0点和12点执行';
                          }
                          return vpsPhysicalMonitorStatus.checkInterval;
                        })()}
                      </Descriptions.Item>
                    </Descriptions>
                    {vpsPhysicalMonitorStatus.description && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: '8px 12px', 
                        backgroundColor: '#f6f8fa', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        {vpsPhysicalMonitorStatus.description}
                      </div>
                    )}
                  </div>
                )}
              </Col>
              <Col span={6} style={{ textAlign: 'right' }}>
                <Space orientation="vertical">
                  {vpsPhysicalMonitorStatus?.isRunning ? (
                    <Button
                      danger
                      icon={<PauseCircleOutlined />}
                      onClick={handleStopVpsPhysicalMonitor}
                      loading={vpsPhysicalMonitorLoading}
                    >
                      停止监控
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleStartVpsPhysicalMonitor}
                      loading={vpsPhysicalMonitorLoading}
                    >
                      启动监控
                    </Button>
                  )}
                  <Button
                    icon={<ThunderboltOutlined />}
                    onClick={handleVpsPhysicalManualCheck}
                  >
                    手动检查
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 统计数据 */}
          {vpsPhysicalStatistics && (
            <>
              {/* 申请统计 */}
              <Card title={<><BarChartOutlined /> 申请统计</>} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="申请总数"
                        value={vpsPhysicalStatistics.requests.total}
                        valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                        prefix={<FileTextOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="已通过"
                        value={vpsPhysicalStatistics.requests.approved}
                        valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="待审核"
                        value={vpsPhysicalStatistics.requests.pending}
                        valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                        prefix={<ClockCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="已拒绝"
                        value={vpsPhysicalStatistics.requests.rejected}
                        valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
                        prefix={<CloseCircleOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
                
                {/* 分类统计 */}
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="VPS申请（已通过）"
                        value={vpsPhysicalStatistics.requests.vpsApproved}
                        valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                        prefix={<CloudServerOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="物理机申请（已通过）"
                        value={vpsPhysicalStatistics.requests.physicalApproved}
                        valueStyle={{ color: '#722ed1', fontSize: '16px' }}
                        prefix={<HddOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>

              {/* IP资源统计 */}
              <Card title={<><DatabaseOutlined /> IP资源统计</>} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="总IP数量"
                        value={vpsPhysicalStatistics.ips.total}
                        valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                        prefix={<GlobalOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="VPS IP"
                        value={vpsPhysicalStatistics.ips.vps}
                        valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                        prefix={<CloudServerOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Statistic
                        title="物理机IP"
                        value={vpsPhysicalStatistics.ips.physical}
                        valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                        prefix={<HddOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* 用户统计 */}
                {vpsPhysicalStatistics.users && Object.keys(vpsPhysicalStatistics.users).length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h4>用户分布统计</h4>
                    <Row gutter={16}>
                      {Object.entries(vpsPhysicalStatistics.users).map(([type, stats]) => (
                        <Col span={12} key={type}>
                          <Card size="small">
                            <Statistic
                              title={`${type === 'vps' ? 'VPS' : '物理机'}用户数`}
                              value={stats.userCount}
                              valueStyle={{ fontSize: '16px' }}
                              suffix={
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  平均 {stats.avgIPsPerUser} IP/用户
                                </div>
                              }
                            />
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </Card>
            </>
          )}

          {/* 最近申请列表 */}
          <Card 
            title={<><FileTextOutlined /> 最近申请</>}
            style={{ marginBottom: 16 }}
            loading={vpsPhysicalDataLoading}
          >
            {vpsPhysicalRecentRequests.length > 0 ? (
              <div>
                {vpsPhysicalRecentRequests.map(request => (
                  <div key={request.id} style={{ 
                    padding: '12px', 
                    border: '1px solid #f0f0f0', 
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>
                    <Row align="middle">
                      <Col span={4}>
                        <div>
                          <Tag color={request.ipType === 'vps' ? 'blue' : 'purple'}>
                            {request.ipType === 'vps' ? 'VPS' : '物理机'}
                          </Tag>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div>
                          <strong>{request.applicant.username}</strong>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {request.applicant.cloudPlatformUID}
                          </div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <Tag color="blue">{request.ipCount} 个IP</Tag>
                      </Col>
                      <Col span={4}>
                        <Tag color={
                          request.status === 'approved' ? 'green' :
                          request.status === 'pending' ? 'orange' : 'red'
                        }>
                          {request.status === 'approved' ? '已通过' :
                           request.status === 'pending' ? '待审核' : '已拒绝'}
                        </Tag>
                      </Col>
                      <Col span={6}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(request.createdAt).toLocaleString('zh-CN')}
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="暂无最近申请记录" />
            )}
          </Card>

          {/* 检查报告 */}
          {vpsPhysicalCheckReport && (
            <Card title={<><FileSearchOutlined /> 检查报告</>} style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Alert
                  message={`数据快照时间：${vpsPhysicalCheckReport.currentSnapshot.timestampFormatted}`}
                  type="info"
                  showIcon
                />
              </div>

              <Row gutter={16}>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="VPS IP总数"
                      value={vpsPhysicalCheckReport.currentSnapshot.totalVpsIPs}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<CloudServerOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="物理机IP总数"
                      value={vpsPhysicalCheckReport.currentSnapshot.totalPhysicalIPs}
                      valueStyle={{ color: '#722ed1' }}
                      prefix={<HddOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="用户总数"
                      value={vpsPhysicalCheckReport.currentSnapshot.totalUsers}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<UserOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="错误率"
                      value={vpsPhysicalCheckReport.trends.errorRate}
                      valueStyle={{ color: vpsPhysicalCheckReport.trends.errorRate > 10 ? '#ff4d4f' : '#52c41a' }}
                      suffix="%"
                      prefix={<ExclamationCircleOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* 建议和警告 */}
              {vpsPhysicalCheckReport.recommendations && vpsPhysicalCheckReport.recommendations.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4>系统建议</h4>
                  {vpsPhysicalCheckReport.recommendations.map((rec, index) => (
                    <Alert
                      key={index}
                      message={rec.message}
                      description={rec.action}
                      type={rec.type}
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* 服务说明卡片 */}
          <Card title={<><InfoCircleOutlined /> 服务说明</>}>
            <Alert
              message="VPS和物理机IP监控服务"
              description="此服务用于监控VPS和物理机类型的IP权限申请，自动检查IP有效性并清理无效配置。监控服务会定期扫描系统中的VPS和物理机IP权限配置，确保IP资源的有效利用。"
              type="info"
              showIcon
            />
          </Card>
        </>
      )}

      {/* 申请详情模态框 */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>IP权限申请详情</span>
          </Space>
        }
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedRequest && (
          <div className="detail-content">
            <Descriptions title="申请人信息" bordered column={2}>
              <Descriptions.Item label="用户名" span={1}>
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  {selectedRequest.applicant.username}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedRequest.applicant.email}</Descriptions.Item>
              <Descriptions.Item label="角色">
                <Tag color="blue">{selectedRequest.applicant.role}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="QQ">
                {selectedRequest.applicant.qq || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="申请时间" span={2}>
                <Space>
                  <CalendarOutlined />
                  {new Date(selectedRequest.createdAt).toLocaleString()}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Descriptions title="申请内容" bordered column={1}>
              <Descriptions.Item label="原始输入">
                <pre className="ip-input-pre">{selectedRequest.ipAddressInput}</pre>
              </Descriptions.Item>
                              <Descriptions.Item label="申请IP">
                  <div>
                    <div style={{ marginBottom: 12 }}>
                      <Tag color="blue" icon={<GlobalOutlined />}>
                        共 {selectedRequest.ipCount} 个IP地址
                      </Tag>
                    </div>
                    <div className="ip-grid-container">
                      {selectedRequest.ipAddresses?.slice(
                        (ipDetailPage - 1) * ipDetailPageSize,
                        ipDetailPage * ipDetailPageSize
                      ).map((ip, index) => (
                        <div key={index} className="ip-grid-item">
                          <span className="ip-index">
                            #{(ipDetailPage - 1) * ipDetailPageSize + index + 1}
                          </span>
                          <code className="ip-code">{ip}</code>
                        </div>
                      ))}
                    </div>
                    {selectedRequest.ipAddresses?.length > ipDetailPageSize && (
                      <div style={{ marginTop: 16, textAlign: 'center' }}>
                        <Pagination
                          current={ipDetailPage}
                          total={selectedRequest.ipAddresses?.length || 0}
                          pageSize={ipDetailPageSize}
                          size="small"
                          showSizeChanger={false}
                          showQuickJumper={false}
                          showTotal={(total, range) => `${range[0]}-${range[1]} / ${total}`}
                          onChange={(page) => setIpDetailPage(page)}
                        />
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
              <Descriptions.Item label="云平台ID">
                <Tag icon={<TeamOutlined />} color="purple">
                  {selectedRequest.cloudPlatformUID}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="IP类型">
                {(() => {
                  const typeMap = {
                    'vps': { text: 'VPS', color: 'blue' },
                    'physical': { text: '物理机', color: 'green' },
                    'colocation': { text: '托管', color: 'orange' }
                  };
                  const config = typeMap[selectedRequest.ipType] || { text: selectedRequest.ipType || 'VPS', color: 'default' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              {selectedRequest.ipType === 'colocation' && selectedRequest.serverId && (
                <Descriptions.Item label="服务器ID">
                  <code style={{ 
                    background: '#f5f5f5', 
                    padding: '2px 6px', 
                    borderRadius: '3px',
                    fontFamily: 'monospace'
                  }}>
                    {selectedRequest.serverId}
                  </code>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="申请理由" className="reason-item">
                <Paragraph>
                  {selectedRequest.applicationReason}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>

            {selectedRequest.status !== 'pending' && (
              <>
                <Divider />
                <Descriptions title="审核信息" bordered column={2}>
                  <Descriptions.Item label="审核状态">
                    <Tag color={getStatusConfig(selectedRequest.status).color} icon={getStatusConfig(selectedRequest.status).icon}>
                      {getStatusConfig(selectedRequest.status).text}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="审核人">
                    {selectedRequest.reviewer?.username || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="审核时间" span={2}>
                    <Space>
                      <CalendarOutlined />
                      {selectedRequest.reviewedAt ? new Date(selectedRequest.reviewedAt).toLocaleString() : '-'}
                    </Space>
                  </Descriptions.Item>
                  {selectedRequest.reviewComment && (
                    <Descriptions.Item label="审核意见" span={2} className="review-comment-item">
                      <Paragraph>
                        {selectedRequest.reviewComment}
                      </Paragraph>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* 单个审核模态框 */}
      <Modal
        title={
          <Space>
            {reviewAction === 'approve' ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            <span>{reviewAction === 'approve' ? '通过申请' : '驳回申请'}</span>
          </Space>
        }
        open={reviewVisible}
        onCancel={() => setReviewVisible(false)}
        confirmLoading={reviewLoading}
        onOk={() => reviewForm.submit()}
      >
        {selectedRequest && (
          <div>
            <Alert
              message={`即将${reviewAction === 'approve' ? '通过' : '驳回'}以下申请`}
              type={reviewAction === 'approve' ? 'success' : 'warning'}
              showIcon
              className="mb-3"
            />
            <Descriptions column={1} size="small">
              <Descriptions.Item label="申请人">{selectedRequest.applicant?.username}</Descriptions.Item>
              <Descriptions.Item label="IP数量">{selectedRequest.ipCount} 个</Descriptions.Item>
              <Descriptions.Item label="IP类型">
                {(() => {
                  const typeMap = {
                    'vps': { text: 'VPS', color: 'blue' },
                    'physical': { text: '物理机', color: 'green' },
                    'colocation': { text: '托管', color: 'orange' }
                  };
                  const config = typeMap[selectedRequest.ipType] || { text: selectedRequest.ipType || 'VPS', color: 'default' };
                  return <Tag color={config.color} size="small">{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              {selectedRequest.ipType === 'colocation' && selectedRequest.serverId && (
                <Descriptions.Item label="服务器ID">
                  <code style={{ fontSize: '12px' }}>{selectedRequest.serverId}</code>
                </Descriptions.Item>
              )}
            </Descriptions>
            <Form
              form={reviewForm}
              layout="vertical"
              onFinish={handleReview}
              className="mt-3"
            >
              <Form.Item
                label={reviewAction === 'approve' ? '审核意见（可选）' : '驳回原因'}
                name="reviewComment"
                rules={reviewAction === 'reject' ? [{ required: true, message: '请输入驳回原因' }] : []}
              >
                <TextArea
                  rows={4}
                  placeholder={reviewAction === 'approve' ? '请输入审核意见...' : '请输入驳回原因...'}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 批量处理模态框 */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            <span>批量处理申请</span>
          </Space>
        }
        open={batchVisible}
        onCancel={() => setBatchVisible(false)}
        confirmLoading={batchLoading}
        onOk={() => batchForm.submit()}
      >
        {(() => {
          const analysis = getSelectedRecordsAnalysis();
          return (
            <>
              <Alert
                message={`已选择 ${analysis.total} 个申请记录，其中 ${analysis.pending} 个待审核记录将被处理`}
                type="info"
                showIcon
                className="mb-3"
              />
              {analysis.hasReviewed && (
                <Alert
                  message={`${analysis.reviewed} 个已审核记录将被跳过（已通过或已拒绝的记录不能重复处理）`}
                  type="warning"
                  showIcon
                  className="mb-3"
                />
              )}
            </>
          );
        })()}
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchReview}
        >
          <Form.Item
            label="处理方式"
            name="action"
            rules={[{ required: true, message: '请选择处理方式' }]}
          >
            <Select placeholder="请选择处理方式">
              <Option value="approve">
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  批量通过
                </Space>
              </Option>
              <Option value="reject">
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  批量驳回
                </Space>
              </Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="处理说明"
            name="reviewComment"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue('action') === 'reject' && !value?.trim()) {
                    return Promise.reject(new Error('批量驳回时必须填写说明'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请输入处理说明..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 删除确认模态框 */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>删除申请记录</span>
          </Space>
        }
        open={deleteVisible}
        onCancel={() => setDeleteVisible(false)}
        confirmLoading={deleteLoading}
        onOk={handleDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        {selectedRequest && (
          <div>
            <Alert
              message="即将删除以下申请记录，此操作不可恢复！"
              type="warning"
              showIcon
              className="mb-3"
            />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="申请人">{selectedRequest.applicant?.username}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedRequest.applicant?.email}</Descriptions.Item>
              <Descriptions.Item label="IP数量">{selectedRequest.ipCount} 个</Descriptions.Item>
              <Descriptions.Item label="IP类型">
                {(() => {
                  const typeMap = {
                    'vps': { text: 'VPS', color: 'blue' },
                    'physical': { text: '物理机', color: 'green' },
                    'colocation': { text: '托管', color: 'orange' }
                  };
                  const config = typeMap[selectedRequest.ipType] || { text: selectedRequest.ipType || 'VPS', color: 'default' };
                  return <Tag color={config.color} size="small">{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              {selectedRequest.ipType === 'colocation' && selectedRequest.serverId && (
                <Descriptions.Item label="服务器ID">
                  <code style={{ fontSize: '12px' }}>{selectedRequest.serverId}</code>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="状态">
                <Tag color={getStatusConfig(selectedRequest.status).color}>
                  {getStatusConfig(selectedRequest.status).text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="申请时间">
                {new Date(selectedRequest.createdAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 批量删除确认模态框 */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>批量删除申请记录</span>
          </Space>
        }
        open={batchDeleteVisible}
        onCancel={() => setBatchDeleteVisible(false)}
        confirmLoading={batchDeleteLoading}
        onOk={handleBatchDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        {(() => {
          const analysis = getSelectedRecordsAnalysis();
          return (
            <div>
              <Alert
                message={`即将删除 ${analysis.total} 个申请记录，此操作不可恢复！`}
                type="warning"
                showIcon
                className="mb-3"
              />
              {(analysis.hasPending || analysis.hasReviewed) && (
                <Alert
                  message={`包含：${analysis.pending} 个待审核记录，${analysis.reviewed} 个已审核记录`}
                  type="info"
                  showIcon
                  className="mb-3"
                />
              )}
              <div>
                <p>被选中的申请记录：</p>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {requests
                    .filter(request => selectedRequestIds.includes(request.id))
                    .map(request => (
                      <div key={request.id} style={{ padding: '8px', border: '1px solid #f0f0f0', marginBottom: '4px', borderRadius: '4px' }}>
                        <div>
                          <strong>{request.applicant?.username}</strong> - {request.ipCount} 个IP
                          {(() => {
                            const typeMap = {
                              'vps': { text: 'VPS', color: 'blue' },
                              'physical': { text: '物理机', color: 'green' },
                              'colocation': { text: '托管', color: 'orange' }
                            };
                            const config = typeMap[request.ipType] || { text: request.ipType || 'VPS', color: 'default' };
                            return (
                              <Tag 
                                color={config.color} 
                                size="small"
                                style={{ marginLeft: '4px' }}
                              >
                                {config.text}
                              </Tag>
                            );
                          })()}
                          <Tag 
                            color={getStatusConfig(request.status).color} 
                            size="small"
                            style={{ marginLeft: '4px' }}
                          >
                            {getStatusConfig(request.status).text}
                          </Tag>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          申请时间：{new Date(request.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

             {/* 自动审批配置模态框 */}
       <Modal
         title={
           <Space>
             <SettingOutlined />
             <span>{isEditingConfig ? '编辑自动审批配置' : '新增自动审批配置'}</span>
           </Space>
         }
         open={configModalVisible}
         onCancel={() => setConfigModalVisible(false)}
         confirmLoading={configSubmitLoading}
         onOk={() => configForm.submit()}
         className="config-modal"
         width={600}
       >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleConfigSubmit}
        >
                     <Form.Item
             label="IP范围"
             name="ipRange"
             rules={[{ required: true, message: '请输入IP范围' }]}
             tooltip="支持单个IP（如192.168.1.1）或CIDR格式（如192.168.1.0/24）"
           >
             <Input
               placeholder="例如：192.168.1.0/24"
               prefix={<GlobalOutlined />}
             />
           </Form.Item>
           <Form.Item
             label="描述"
             name="description"
             rules={[{ required: true, message: '请输入配置描述' }]}
           >
             <TextArea
               rows={3}
               placeholder="例如：内部网络自动审批范围"
               maxLength={200}
               showCount
             />
           </Form.Item>
           <Form.Item
             label="启用状态"
             name="isEnabled"
             valuePropName="checked"
             initialValue={true}
           >
             <Switch
               checkedChildren="启用"
               unCheckedChildren="禁用"
             />
           </Form.Item>
        </Form>
      </Modal>

      {/* 删除配置确认模态框 */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>删除自动审批配置</span>
          </Space>
        }
        open={configDeleteVisible}
        onCancel={() => setConfigDeleteVisible(false)}
        confirmLoading={configDeleteLoading}
        onOk={handleConfigDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        {selectedConfig && (
          <div>
            <Alert
              message="即将删除以下自动审批配置，此操作不可恢复！"
              type="warning"
              showIcon
              className="mb-3"
            />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="IP范围">{selectedConfig.ipRange}</Descriptions.Item>
              <Descriptions.Item label="描述">{selectedConfig.description}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedConfig.isEnabled ? 'green' : 'red'}>
                  {selectedConfig.isEnabled ? '已启用' : '已禁用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(selectedConfig.createdAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 批量删除配置确认模态框 */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>批量删除自动审批配置</span>
          </Space>
        }
        open={batchConfigDeleteVisible}
        onCancel={() => setBatchConfigDeleteVisible(false)}
        confirmLoading={batchConfigDeleteLoading}
        onOk={handleBatchConfigDelete}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        {(() => {
          const analysis = getSelectedConfigAnalysis();
          return (
            <div>
              <Alert
                message={`即将删除 ${analysis.total} 个自动审批配置，此操作不可恢复！`}
                type="warning"
                showIcon
                className="mb-3"
              />
              {(analysis.hasDisabled || analysis.hasEnabled) && (
                <Alert
                  message={`包含：${analysis.disabled} 个已禁用配置，${analysis.enabled} 个已启用配置`}
                  type="info"
                  showIcon
                  className="mb-3"
                />
              )}
              <div>
                <p>被选中的自动审批配置：</p>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {autoConfigs
                    .filter(config => selectedConfigIds.includes(config.id))
                    .map(config => (
                      <div key={config.id} style={{ padding: '8px', border: '1px solid #f0f0f0', marginBottom: '4px', borderRadius: '4px' }}>
                        <div>
                          <strong>{config.ipRange}</strong> - {config.description}
                          <Tag 
                            color={config.isEnabled ? 'green' : 'red'} 
                            size="small"
                            style={{ marginLeft: '8px' }}
                          >
                            {config.isEnabled ? '已启用' : '已禁用'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          创建时间：{new Date(config.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

             {/* 测试IP模态框 */}
       <Modal
         title={
           <Space>
             <ExperimentOutlined />
             <span>测试IP</span>
           </Space>
         }
         open={testIPVisible}
         onCancel={() => setTestIPVisible(false)}
         onOk={() => testIPForm.submit()}
         okText="测试"
         cancelText="取消"
       >
         <div>
           <Alert
             message="输入IP地址，测试是否在自动审批范围内"
             type="info"
             showIcon
             className="mb-3"
           />
           <Form
             form={testIPForm}
             layout="vertical"
             onFinish={handleTestIP}
           >
             <Form.Item
               label="IP地址"
               name="ip"
               rules={[
                 { required: true, message: '请输入IP地址' },
                 {
                   pattern: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
                   message: '请输入有效的IP地址格式'
                 }
               ]}
             >
               <Input
                 placeholder="例如：192.168.1.1"
                 prefix={<GlobalOutlined />}
               />
             </Form.Item>
           </Form>
         </div>
       </Modal>
     </div>
   );
};

export default IPPermissionManagement; 